import { getEffectiveUnit } from 'utils/measurement';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useMount } from 'ahooks';
import { useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { EqptType, Equipment } from 'domains/eqptDomain';
import { PartialSKU, SKU } from 'domains/skuDomain';
import { useEffect, useMemo, useState } from 'react';
import skuService from 'services/skuService';
import { computeSkuDiffs } from 'utils/review';

type Props = {
  sku: PartialSKU;
};
export const DiffButton = ({ sku }: Props) => {
  // Required by computeSkuDiffs, and in the memo's deps, so the diff re-renders on a switch.
  const { unit } = useMeasurementUnit();
  const eqptTypes = useAppSelector((state) => state.eqpt.eqptTypes);
  const [exceptEqpts, setExceptEqpts] = useState<Equipment[]>([]);
  const [mainExceptEqpts, setMainExceptEqpts] = useState<Equipment[]>([]);
  const [mainSku, setMainSku] = useState<SKU>();
  const [loading, setLoading] = useState(false);

  useMount(async () => {
    setLoading(true);
    const main = await skuService.startViewSku(sku);
    setMainSku(main);
    setLoading(false);
  });

  useEffect(() => {
    const updateExceptEqpts = async () => {
      setLoading(true);
      if (mainSku) {
        const [exceptEqpts, mainExceptEqpts] = await Promise.all([
          skuService.getExceptEqpts(sku),
          skuService.getExceptEqpts(mainSku),
        ]);
        setExceptEqpts(exceptEqpts);
        setMainExceptEqpts(mainExceptEqpts);
      } else {
        const exceptEqpts = await skuService.getExceptEqpts(sku);
        setExceptEqpts(exceptEqpts);
      }
      setLoading(false);
    };
    updateExceptEqpts();
  }, [mainSku, sku]);

  const diffs = useMemo(() => {
    if (loading) {
      return [];
    }
    return computeSkuDiffs(
      sku,
      mainSku,
      eqptTypes,
      exceptEqpts,
      mainExceptEqpts,
      false,
      unit
    );
  }, [eqptTypes, exceptEqpts, loading, mainExceptEqpts, mainSku, sku, unit]);

  console.log('diffs', diffs);
  return (
    <RevxButton type="link" size="small" loading={loading}>
      Show Diff ({diffs.length})
    </RevxButton>
  );
};

export const computeDiff = async (
  sku: PartialSKU,
  eqptTypes: EqptType[],
  main?: PartialSKU
) => {
  let mainSku = main;
  if (!mainSku) {
    mainSku = await skuService.startViewSku(sku);
  }

  const [exceptEqpts, mainExceptEqpts] = await Promise.all([
    skuService.getExceptEqpts(sku),
    skuService.getExceptEqpts(mainSku),
  ]);

  return computeSkuDiffs(
    sku,
    mainSku,
    eqptTypes,
    exceptEqpts,
    mainExceptEqpts,
    false,
    // Not a component, so there is no hook to read: this one is computed on demand rather than
    // rendered, and the module-level unit is the same one the API layer stamps on every request.
    getEffectiveUnit()
  );
};
